import React from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import { SHORTCUT_PALETTE } from '../constants';

const TopBar = () => {
  const { 
    viewMode, setViewMode, setIsPaletteOpen, focusMode, setFocusMode,
    isSyncing, runSync, syncState, syncSettings
  } = useStore();

  const views = [
    { id: 'board', icon: 'Grid', label: 'Board' },
    { id: 'list', icon: 'List', label: 'List' },
    { id: 'timeline', icon: 'Clock', label: 'Timeline' }
  ];

  const getSyncTooltip = () => {
    if (syncState.status === 'syncing') return 'Syncing with Google Sheets...';
    if (syncState.status === 'error') return `Sync Error: ${syncState.error}`;
    if (syncState.lastSyncAt) return `Last synced: ${new Date(syncState.lastSyncAt).toLocaleTimeString()}`;
    return 'Click to sync with Google Sheets';
  };

  if (focusMode) {
    return (
      <div className="fixed top-4 right-4 z-50">
        <button onClick={() => setFocusMode(false)} className="bg-gray-800 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 hover:bg-gray-700 transition" >
          <SafeIcon name="Minimize" /> Exit Focus Mode
        </button>
      </div>
    );
  }

  return (
    <div className="h-14 border-b border-gray-200 bg-white/80 backdrop-blur shrink-0 flex items-center justify-between px-6 sticky top-0 z-10">
      <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-lg">
        {viewMode === 'dashboard' ? (
          <div className="px-3 py-1.5 text-sm font-bold text-gray-900">Dashboard</div>
        ) : (
          views.map(v => (
            <button key={v.id} onClick={() => setViewMode(v.id)} className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === v.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'}`} >
              <SafeIcon name={v.icon} className="mr-2 text-xs" /> {v.label}
            </button>
          ))
        )}
      </div>

      <div className="flex items-center gap-3">
        {syncSettings.scriptUrl && (
          <button 
            onClick={() => runSync('manual')}
            disabled={isSyncing}
            title={getSyncTooltip()}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
              syncState.status === 'error' 
                ? 'bg-red-50 border-red-200 text-red-600' 
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <SafeIcon 
              name="RefreshCw" 
              className={`text-xs ${isSyncing ? 'animate-spin text-blue-500' : ''}`} 
            />
            <span className="hidden md:inline">
              {isSyncing ? 'Syncing...' : syncState.status === 'error' ? 'Sync Failed' : 'Sync'}
            </span>
          </button>
        )}

        <button onClick={() => setIsPaletteOpen(true)} className="flex items-center px-4 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-500 transition-colors w-64" >
          <SafeIcon name="Search" className="mr-2" />
          <span className="flex-1 text-left">Search or commands...</span>
          <span className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white shadow-sm">{SHORTCUT_PALETTE}</span>
        </button>

        <button onClick={() => setFocusMode(true)} title="Focus Mode (Ctrl+Shift+F)" className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors" >
          <SafeIcon name="Maximize" />
        </button>
      </div>
    </div>
  );
};

export default TopBar;